import { getApiBase } from './api';

export interface UserBranding {
  id?: string;
  company_name: string;
  logo_url: string | null;
  primary_color: string;
  secondary_color: string;
  text_color: string;
  display_type: 'logo_only' | 'name_only' | 'logo_and_name';
}

export interface QRBranding {
  showBranding: boolean;
  companyName?: string;
  logoUrl?: string | null;
  primaryColor?: string;
  secondaryColor?: string;
  textColor?: string;
  displayType?: 'logo_only' | 'name_only' | 'logo_and_name';
}

/**
 * Get the appropriate branding based on the QR link's branding_type
 * @param brandingType - The branding type from the QR link
 * @param authorId - The author/user ID who created the QR link
 * @returns The branding configuration to use
 */
export async function getQRBranding(
  brandingType: 'beyondpages' | 'custom' | 'none' | undefined,
  authorId?: string
): Promise<QRBranding> {
  const API_BASE = getApiBase();
  
  console.log('🎨 BRANDING DEBUG - getQRBranding called with:', { brandingType, authorId });
  
  // Default to BeyondPages branding if not specified
  if (!brandingType || brandingType === 'beyondpages') {
    console.log('🎨 BRANDING DEBUG - Using BeyondPages branding (default)');
    return {
      showBranding: true,
      companyName: 'BeyondPages',
      logoUrl: null, // Will use the default Logo component
      primaryColor: '#0055AA',
      secondaryColor: '#1F2937',
      textColor: '#FFFFFF',
      displayType: 'logo_and_name'
    };
  }
  
  // No branding
  if (brandingType === 'none') {
    console.log('🎨 BRANDING DEBUG - Using no branding');
    return {
      showBranding: false
    };
  }
  
  // Custom branding - load from user's branding settings
  if (brandingType === 'custom' && authorId) {
    console.log('🎨 BRANDING DEBUG - Attempting to load custom branding for author:', authorId);
    try {
      const brandingUrl = `${API_BASE}/api/branding/branding/${authorId}`;
      console.log('🎨 BRANDING DEBUG - Fetching from URL:', brandingUrl);
      
      const response = await fetch(brandingUrl);
      console.log('🎨 BRANDING DEBUG - Branding API response status:', response.status);
      
      if (response.ok) {
        const userBranding: UserBranding = await response.json();
        console.log('🎨 BRANDING DEBUG - Received user branding:', userBranding);
        
        // Logo URL should now always be a full Supabase Storage URL
        let logoUrl = null;
        if (userBranding.logo_url) {
          console.log('🎨 LOGO URL DEBUG - Original logo_url:', userBranding.logo_url);
          
          if (userBranding.logo_url.startsWith('http://') || userBranding.logo_url.startsWith('https://')) {
            // Already a full URL (Supabase Storage URL)
            logoUrl = userBranding.logo_url;
            console.log('🎨 LOGO URL DEBUG - Using Supabase Storage URL:', logoUrl);
          } else {
            // Legacy relative path - this shouldn't happen with new uploads
            logoUrl = `${API_BASE}${userBranding.logo_url}`;
            console.log('🎨 LOGO URL DEBUG - Legacy path, using API_BASE:', logoUrl);
          }
        } else {
          console.log('🎨 LOGO URL DEBUG - No logo_url provided');
        }
        
        const finalBranding = {
          showBranding: true,
          companyName: userBranding.company_name,
          logoUrl: logoUrl,
          primaryColor: userBranding.primary_color,
          secondaryColor: userBranding.secondary_color,
          textColor: userBranding.text_color,
          displayType: userBranding.display_type
        };
        
        console.log('🎨 BRANDING DEBUG - Returning custom branding:', finalBranding);
        return finalBranding;
      } else {
        console.log('🎨 BRANDING DEBUG - Branding API failed with status:', response.status);
      }
    } catch (error) {
      console.error('🎨 BRANDING DEBUG - Error loading custom branding:', error);
    }
  } else {
    console.log('🎨 BRANDING DEBUG - Custom branding skipped - brandingType:', brandingType, 'authorId:', authorId);
  }
  
  // Fallback to BeyondPages branding if custom branding fails
  console.log('🎨 BRANDING DEBUG - Falling back to BeyondPages branding');
  return {
    showBranding: true,
    companyName: 'BeyondPages',
    logoUrl: null,
    primaryColor: '#0055AA',
    secondaryColor: '#1F2937',
    textColor: '#FFFFFF',
    displayType: 'logo_and_name'
  };
} 